"use client";

import type React from "react";
import { useState, useEffect, useRef } from "react";
import type SignatureCanvas from "react-signature-canvas";
import { motion } from "framer-motion";
import { Clock, Dumbbell, Award, Shield } from "lucide-react";
import type {
  FormData,
  Influencer,
  User,
  Fee,
  MembershipBenefit as MembershipBenefitType,
  AlcoholOption,
} from "./form-sections/types";
import { PersonalInfoSection } from "./form-sections/personal-info-section";
import { AddressInfoSection } from "./form-sections/address-info-section";
import { HealthQuestionnaireSection } from "./form-sections/health-questionnaire-section";
import { ClausesRegulationsSection } from "./form-sections/clauses-regulations-section";
import { MembershipInfoSection } from "./form-sections/membership-info-section";
import { PaymentSection } from "./form-sections/payment-section";
import { InfoVideoSection } from "./form-sections/info-video-section";
import { SignatureSection } from "./form-sections/signature-section";
import { DetailedHealthQuestionnaireSection } from "./form-sections/detailed-health-questionnaire-section";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  useRegisterAppSetupFormMutation,
  useRegisterAppSetupQuery,
} from "./api/RegisterAppUser.api";

// Placeholder data (replace with actual data from your backend)
const influencerData: Influencer = {
  id: "123",
  branch: {
    logo: "/fitness-logo.png",
    branch: "Centro de Fitness",
    clauses_user: "/path/to/clauses.pdf",
    contract: "/path/to/contract.pdf",
    contract_user: true,
    gateway: "stripe",
  },
};

const userData: User = {
  id: "456",
  name: "Juan Pérez",
  email: "juan.perez@example.com",
  branch: { gateway: "stripe" },
  other: "",
};

const membershipBenefitsData: MembershipBenefitType[] = [
  {
    title: "Acceso ilimitado",
    description: "Entrena cuando quieras, sin restricciones de horario",
    icon: <Clock className="w-5 h-5 text-purple-500" />,
  },
  {
    title: "Equipamiento premium",
    description: "Máquinas de última generación y áreas especializadas",
    icon: <Dumbbell className="w-5 h-5 text-purple-500" />,
  },
  {
    title: "Entrenadores certificados",
    description: "Asesoramiento profesional para maximizar tus resultados",
    icon: <Award className="w-5 h-5 text-purple-500" />,
  },
  {
    title: "Garantía de satisfacción",
    description: "30 días de prueba con devolución garantizada",
    icon: <Shield className="w-5 h-5 text-purple-500" />,
  },
];

// Opciones para el selector de alcohol
const optionsAlcoholData: AlcoholOption[] = [
  { value: "Si", label: "Sí" },
  { value: "No", label: "No" },
  { value: "Esporadico", label: "Esporádico" },
];

const slideUp = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

const FormValuationPage: React.FC = () => {
  const [searchParams] = useSearchParams();

  const token = searchParams.get("token");
  const fee = searchParams.get("fee");
  const branch = searchParams.get("branch");

  const { data } = useRegisterAppSetupQuery({
    token: token,
    fee: fee,
    branch_id: branch,
  });

  const feeData = data?.subscriptionDetails;

  const preloadData = data?.branchInfo;

  const [
    formPostData,
    {
      data: resultFormPost,
      isLoading: isLoadingFormPost,
      isError: isErrorFormPost,
    },
  ] = useRegisterAppSetupFormMutation();

  const navigate = useNavigate();

  const [formData, setFormData] = useState<FormData>({
    fullName: userData.name || "",
    email: userData.email,
    sex: "Hombre",
    birthday: "",
    phone: "",
    branch_id: branch,
    identity_number: "",
    address: "",
    country: "",
    state: "",
    city: "",
    postal_code: "",
    cardiac_condition: false,
    physical_limitation: false,
    pregnancy_risk: false,
    serious_injuries: false,
    medical_problem_description: "",
    other: userData.other || "",
    accept_clauses: true,
    sufficiently_informed: true,
    receive_communications: false,
    image_rights: false,
    signature: "",
    fumador: false,
    selectedAlcohol: "No",
    hours_sleep: "7",
    dormirBien: true,
    tomarMedicamentos: false,
    medication_description: "",
    problemaMedico: false,
    medical_problem_description_detailed: "",
    analiticaAlterada: false,
    last_analytic_altered_description: "",
    presionAlterada: false,
    altered_tension_description: "",
    affectPeriod: false,
    amenorrea: false,
    irregulaPeriod: false,
    irregular_period_frequency: "",
    riskStatus: false,
    seriousInjuries_detailed: false,
    ankle: false,
    knee: false,
    hip: false,
    elbow: false,
    lumbar: false,
    cervical: false,
    deltoid: false,
    hand: false,
    influencer_id: token || "",
    other_injuries: "",
    sitting_hours: "8",
    activtyFisical: true,
    physical_activity_description: "",
    days_week_train: "3",
    training_experience: "Intermedio",
    training_with_trainer: "A veces",
    describe_week_training: "",
    improvements: "",
    equipeGym: true,
    well_equipped_gym_description: "",
    usual_training_time: "Tarde",
    foods_not_like: "",
    intolerance: false,
    intolerance_description: "",
    food_preferences: "",
    how_may_meal_day: "3",
    meals_in_home_and_outside: "",
    foods_in_home_and_outside: "",
    supplementation: false,
    supplementation_description: "",
    useSupplementation: false,
  });

  useEffect(() => {
    if (!preloadData) return;
    setFormData((prev) => ({
      ...prev,
      country: preloadData.country || "",
      state: preloadData.state || "",
      city: preloadData.population || "",
      address: preloadData.adress || "",
      postal_code: preloadData.codeZip || "",
      phone: preloadData.phone || "",
      email: preloadData.email || prev.email,
    }));
  }, [preloadData]);

  const [showHealthWarning, setShowHealthWarning] = useState(false);
  const signatureRef = useRef<SignatureCanvas>(null);
  const signaturePadContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateSignaturePadSize = () => {
      if (signaturePadContainerRef.current && signatureRef.current) {
        const canvas = signatureRef.current.getCanvas();
        const container = signaturePadContainerRef.current;
        const containerWidth = container.clientWidth;
        // Ensure containerHeight is not 0, provide a default or minimum if necessary
        const containerHeight =
          container.clientHeight > 0 ? container.clientHeight : 128; // 128px as h-32
        const dpr = window.devicePixelRatio || 1;

        canvas.width = containerWidth * dpr;
        canvas.height = containerHeight * dpr;

        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.scale(dpr, dpr);
        }

        canvas.style.width = `${containerWidth}px`;
        canvas.style.height = `${containerHeight}px`;
      }
    };
    updateSignaturePadSize();
    window.addEventListener("resize", updateSignaturePadSize);
    return () => window.removeEventListener("resize", updateSignaturePadSize);
  }, []);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };


  useEffect(() => {
    console.log("hola como estas", resultFormPost?.userId);
    if (resultFormPost?.userId) {
      navigate(`/users/${resultFormPost?.userId}`);
    }
  }, [resultFormPost]);

  useEffect(() => {
    setShowHealthWarning(
      formData.cardiac_condition ||
        formData.physical_limitation ||
        formData.pregnancy_risk ||
        formData.serious_injuries
    );
  }, [
    formData.cardiac_condition,
    formData.physical_limitation,
    formData.pregnancy_risk,
    formData.serious_injuries,
  ]);

  const isPaymentEnabled =
    formData.sex &&
    formData.fullName &&
    formData.birthday &&
    formData.identity_number;

  const handleClearSignature = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
      setFormData((prev) => ({ ...prev, signature: "" }));
    }
  };

  const handleSignatureEnd = () => {
    if (signatureRef.current && !signatureRef.current.isEmpty()) {
      const signatureData = signatureRef.current.toDataURL("image/png");
      setFormData((prev) => ({ ...prev, signature: signatureData }));
    } else {
      setFormData((prev) => ({ ...prev, signature: "" }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Implement actual submission logic (e.g., API call)
    console.log("Form submitted:", formData);
  };

  const conditionalTextareaClass = " focus:border-red-700";

  return (
    <div className="min-h-screen bg-white text-gray-800 py-6">
      <motion.form
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
        onSubmit={handleSubmit}
        className="container mx-auto px-4 max-w-5xl"
      >
        <input type="hidden" name="influencer_id" value={influencerData.id} />
        <input type="hidden" name="user_id" value={userData.id} />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="space-y-6">
            <PersonalInfoSection
              formData={formData}
              handleChange={handleChange}
            />
            <AddressInfoSection
              formData={formData}
              handleChange={handleChange}
            />
            <HealthQuestionnaireSection
              formData={formData}
              handleChange={handleChange}
              showHealthWarning={showHealthWarning}
              conditionalTextareaClass={conditionalTextareaClass}
            />
            <ClausesRegulationsSection
              formData={formData}
              handleChange={handleChange}
              influencer={influencerData}
            />
          </div>

          <div className="space-y-6">
            {feeData && (
              <MembershipInfoSection
                fee={feeData}
                membershipBenefits={membershipBenefitsData}
              />
            )}

            <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-xl overflow-hidden shadow-xl">
              {feeData && (
                <PaymentSection user={userData} handleChange={handleChange} />
              )}
            </div>
            <InfoVideoSection
              videoId="7wKsMmn6zSU"
              title="Tutorial App sin Algoritmo"
            />
            <SignatureSection
              signatureRef={signatureRef}
              signaturePadContainerRef={signaturePadContainerRef}
              handleSignatureEnd={handleSignatureEnd}
              handleClearSignature={handleClearSignature}
            />
          </div>
        </div>

        <div className="mb-6">
          <DetailedHealthQuestionnaireSection
            formData={formData}
            handleChange={handleChange}
            optionsAlcohol={optionsAlcoholData}
            conditionalTextareaClass={conditionalTextareaClass}
          />
        </div>

        <motion.div variants={slideUp} className="mt-8 mb-40">
          <button
            onClick={() => {
              formPostData(formData);
           

              if (resultFormPost?.userId) {
                navigate(`/users/${resultFormPost?.userId}`);
              }
            }}
            type="submit"
            disabled={!isPaymentEnabled || isLoadingFormPost}
            className={`relative w-full bg-primary text-white font-medium py-3 px-8 rounded-md transition-colors duration-200 
      ${!isPaymentEnabled || isLoadingFormPost ? "opacity-50 cursor-not-allowed" : "hover:bg-primary"}`}
          >
            {/* Texto (oculto mientras carga) */}
            <span className={`${isLoadingFormPost ? "invisible" : "visible"}`}>
              {feeData ? "Registrar y Pagar" : "Registrar"}
            </span>

            {/* Spinner */}
            {isLoadingFormPost && (
              <span className="absolute inset-0 flex items-center justify-center">
                <svg
                  className="w-5 h-5 animate-spin text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  />
                </svg>
              </span>
            )}
          </button>
        </motion.div>
      </motion.form>
    </div>
  );
};

export default FormValuationPage;
